import { useState, useRef, useCallback, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/contexts/auth";
import { useLocation, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, Clock, Users, Trash2, AlertTriangle, Zap, ShieldCheck, Crown, Settings, BarChart3, Mail, Shield, Search, X, Download, Phone, Globe, ChevronDown, Lock, LockOpen, SlidersHorizontal, ArrowUpDown, RotateCcw, Eye, CreditCard, Megaphone, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { UserStatsModal } from "@/components/UserStatsModal";
import { UserMessageModal } from "@/components/UserMessageModal";
import { TRACK_INFO, type LicenseTrack } from "@/contexts/track";

type PlanValue = "free" | "pro" | "pro_plus";

function normalizeTierPlan(raw: string | null | undefined): PlanValue {
  if (raw === "pro" || raw === "pro_plus") return raw;
  return "free";
}

interface UserRecord {
  id: number;
  fullName: string;
  email: string;
  status: "pending" | "approved" | "rejected";
  role: string;
  plan: PlanValue;
  pplPlan?: PlanValue;
  address: string | null;
  phone: string | null;
  country: string | null;
  gender: string | null;
  age: number | null;
  pilotType: string | null;
  ratings: string[] | null;
  createdAt: string;
  lockedAt: string | null;
  lockReason: string | null;
  failedLoginAttempts: number;
  passwordChangedAt: string | null;
}


type FilterTab = "pending" | "approved" | "rejected" | "all" | "admins" | "moderators";

type AdminMainTab = "users" | "subscriptions" | "programStats";

type ProgramTrackRow = {
  track: LicenseTrack;
  enabled: boolean;
  questionsInBank: number;
  distinctLearners: number;
  completedSessions: number;
};

type ProgramStatsPayload = {
  totals: { approvedUsers: number; pendingUsers: number };
  tracks: ProgramTrackRow[];
  cplPlanAmongApprovedLearners: Record<PlanValue, number>;
  pplPlanAmongApprovedLearners: Record<PlanValue, number>;
};

async function fetchWithAuth(path: string, options?: RequestInit) {
  const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const PLAN_META = {
  free: {
    label: "FREE",
    badgeClass: "bg-white/10 text-white/50 border-white/20",
    icon: null,
  },
  pro: {
    label: "PRO",
    badgeClass: "bg-primary/20 text-primary border-primary/30",
    icon: Zap,
  },
  pro_plus: {
    label: "PRO PLUS",
    badgeClass: "bg-amber-500/20 text-amber-300 border-amber-500/30",
    icon: Crown,
  },
} as const;

function PlanBadge({ plan }: { plan: "free" | "pro" | "pro_plus" }) {
  const meta = PLAN_META[plan] ?? PLAN_META.free;
  const Icon = meta.icon;
  return (
    <Badge className={cn("font-mono text-xs border gap-1", meta.badgeClass)}>
      {Icon && <Icon className="w-2.5 h-2.5" />}
      {meta.label}
    </Badge>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [updating, setUpdating] = useState<number | null>(null);
  const [mainTab, setMainTab] = useState<AdminMainTab>("users");
  const [activeTab, setActiveTab] = useState<FilterTab>("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState<"all" | "free" | "pro" | "pro_plus">("all");
  const [filterLock, setFilterLock] = useState<"all" | "locked" | "unlocked">("all");
  const [filterPilotType, setFilterPilotType] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "name_asc" | "name_desc" | "attempts">("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<UserRecord | null>(null);
  const [confirmMakeAdmin, setConfirmMakeAdmin] = useState<UserRecord | null>(null);
  const [statsUser, setStatsUser] = useState<UserRecord | null>(null);
  const [messageUser, setMessageUser] = useState<UserRecord | null>(null);

  if (!["admin", "master_admin"].includes(user?.role ?? "")) {
    setLocation("/");
    return null;
  }

  const currentUserIsMasterAdmin = user?.role === "master_admin";

  // Fetch all users including admins
  const { data: allUsers = [], isLoading } = useQuery<UserRecord[]>({
    queryKey: ["admin-users"],
    queryFn: () => fetchWithAuth("/api/admin/users?includeAdmins=true") as Promise<UserRecord[]>,
  });

  const regularUsers = allUsers.filter(u => !["admin", "master_admin"].includes(u.role));
  const adminUsers = allUsers.filter(u => ["admin", "master_admin"].includes(u.role));
  const moderatorUsers = allUsers.filter(u => u.role === "moderator");
  const masterAdminCount = allUsers.filter(u => u.role === "master_admin").length;

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      fetchWithAuth(`/api/admin/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onMutate: ({ id }) => setUpdating(id),
    onSettled: () => {
      setUpdating(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const updatePlan = useMutation({
    mutationFn: ({ id, plan, track }: { id: number; plan: string; track: "cpl" | "ppl" }) =>
      fetchWithAuth(`/api/admin/users/${id}/plan`, {
        method: "PATCH",
        body: JSON.stringify({ plan, track }),
      }),
    onMutate: ({ id }) => setUpdating(id),
    onSettled: () => {
      setUpdating(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: number; role: string }) =>
      fetchWithAuth(`/api/admin/users/${id}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      }),
    onMutate: ({ id }) => setUpdating(id),
    onSettled: () => {
      setUpdating(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const deleteUser = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/admin/users/${id}`, { method: "DELETE" }),
    onMutate: (id) => setUpdating(id),
    onSettled: () => {
      setUpdating(null);
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const lockUser = useMutation({
    mutationFn: ({ id, action }: { id: number; action: "lock" | "unlock" }) =>
      fetchWithAuth(`/api/admin/users/${id}/${action}`, { method: "POST" }),
    onMutate: ({ id }) => setUpdating(id),
    onSettled: () => {
      setUpdating(null);
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["admin-settings"],
    queryFn: () => fetchWithAuth("/api/admin/settings") as Promise<Record<string, string>>,
  });
  const { data: programStats, isLoading: programStatsLoading } = useQuery<ProgramStatsPayload>({
    queryKey: ["admin-program-stats"],
    queryFn: () => fetchWithAuth("/api/admin/program-stats") as Promise<ProgramStatsPayload>,
    enabled: mainTab === "programStats",
  });
  const requireApproval = settings?.require_approval !== "false";

  useEffect(() => {
    if (mainTab !== "users" || requireApproval) return;
    void fetchWithAuth("/api/admin/ack-new-registrations", { method: "POST" })
      .then(() => qc.invalidateQueries({ queryKey: ["sidebar-counts"] }))
      .catch(() => {});
  }, [mainTab, requireApproval, qc]);

  const subscriptionStyle = settings?.subscription_style === "classic"
    ? "classic"
    : settings?.subscription_style === "platinum"
      ? "platinum"
      : "premium";

  const updateSetting = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      fetchWithAuth("/api/admin/settings", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["admin-program-stats"] });
    },
  });

  const pending = regularUsers.filter(u => u.status === "pending");
  const approved = regularUsers.filter(u => u.status === "approved");
  const rejected = regularUsers.filter(u => u.status === "rejected");

  const baseList =
    activeTab === "admins" ? adminUsers :
    activeTab === "moderators" ? moderatorUsers :
    activeTab === "all" ? regularUsers :
    activeTab === "pending" ? pending :
    activeTab === "approved" ? approved :
    rejected;

  const q = searchQuery.trim().toLowerCase();

  const uniqueCountries = Array.from(new Set(regularUsers.map(u => u.country).filter(Boolean) as string[])).sort();
  const uniquePilotTypes = Array.from(new Set(regularUsers.map(u => u.pilotType).filter(Boolean) as string[])).sort();

  const activeFilterCount = [
    filterPlan !== "all" ? 1 : 0,
    filterLock !== "all" ? 1 : 0,
    filterPilotType !== "all" ? 1 : 0,
    filterCountry !== "all" ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const filteredUsers = baseList
    .filter(u => {
      if (q && !u.fullName.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q) &&
          !u.country?.toLowerCase().includes(q) && !u.pilotType?.toLowerCase().includes(q)) return false;
      if (filterPlan !== "all" && u.plan !== filterPlan) return false;
      if (filterLock === "locked" && !u.lockedAt) return false;
      if (filterLock === "unlocked" && u.lockedAt) return false;
      if (filterPilotType !== "all" && u.pilotType !== filterPilotType) return false;
      if (filterCountry !== "all" && u.country !== filterCountry) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "newest") return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortBy === "oldest") return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortBy === "name_asc") return a.fullName.localeCompare(b.fullName);
      if (sortBy === "name_desc") return b.fullName.localeCompare(a.fullName);
      if (sortBy === "attempts") return (b.failedLoginAttempts ?? 0) - (a.failedLoginAttempts ?? 0);
      return 0;
    });

  function resetFilters() {
    setSearchQuery("");
    setFilterPlan("all");
    setFilterLock("all");
    setFilterPilotType("all");
    setFilterCountry("all");
    setSortBy("newest");
  }

  const userFilterTabs: {
    key: FilterTab;
    label: string;
    count: number;
    icon: typeof Clock;
    accent: string;
    selectedClass: string;
  }[] = [
    {
      key: "pending",
      label: "Pending",
      count: pending.length,
      icon: Clock,
      accent: "text-yellow-400",
      selectedClass: "border-yellow-400/80 bg-yellow-500/15 text-yellow-400 ring-1 ring-yellow-400/40",
    },
    {
      key: "approved",
      label: "Approved",
      count: approved.length,
      icon: CheckCircle2,
      accent: "text-primary",
      selectedClass: "border-primary/80 bg-primary/15 text-primary ring-1 ring-primary/40",
    },
    {
      key: "rejected",
      label: "Rejected",
      count: rejected.length,
      icon: XCircle,
      accent: "text-destructive",
      selectedClass: "border-destructive/80 bg-destructive/15 text-destructive ring-1 ring-destructive/40",
    },
    {
      key: "all",
      label: "All users",
      count: regularUsers.length,
      icon: Users,
      accent: "text-foreground",
      selectedClass: "border-foreground/50 bg-secondary text-foreground ring-1 ring-foreground/25",
    },
    {
      key: "moderators",
      label: "Moderators",
      count: moderatorUsers.length,
      icon: ShieldCheck,
      accent: "text-blue-400",
      selectedClass: "border-blue-400/80 bg-blue-500/15 text-blue-400 ring-1 ring-blue-400/40",
    },
    {
      key: "admins",
      label: "Admins",
      count: adminUsers.length,
      icon: Shield,
      accent: "text-amber-300",
      selectedClass: "border-amber-400/80 bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/40",
    },
  ];

  const statusBadge = (status: string) => {
    if (status === "approved") return <Badge className="bg-primary/20 text-primary border-primary/30 font-mono text-xs">APPROVED</Badge>;
    if (status === "rejected") return <Badge className="bg-destructive/20 text-destructive border-destructive/30 font-mono text-xs">REJECTED</Badge>;
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 font-mono text-xs">PENDING</Badge>;
  };

  const PlanControls = ({ u, billingTrack }: { u: UserRecord; billingTrack: "cpl" | "ppl" }) => {
    const currentPlan: PlanValue = billingTrack === "ppl" ? normalizeTierPlan(u.pplPlan) : normalizeTierPlan(u.plan);
    const plans: Array<{ key: PlanValue; label: string; icon?: typeof Zap }> = [
      { key: "free", label: "Free" },
      { key: "pro", label: "Pro", icon: Zap },
      { key: "pro_plus", label: "Pro Plus", icon: Crown },
    ];

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-muted-foreground/50 font-mono shrink-0">
          {billingTrack === "ppl" ? "PPL" : "CPL"}:
        </span>
        {plans.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            disabled={updating === u.id || currentPlan === key}
            onClick={() => updatePlan.mutate({ id: u.id, plan: key, track: billingTrack })}
            className={cn(
              "inline-flex items-center gap-1 text-xs font-mono font-bold px-2 py-1 rounded border transition-all",
              currentPlan === key
                ? key === "pro_plus"
                  ? "border-amber-400/50 bg-amber-400/15 text-amber-300 cursor-default"
                  : key === "pro"
                    ? "border-primary/50 bg-primary/15 text-primary cursor-default"
                    : "border-white/20 bg-white/10 text-white/50 cursor-default"
                : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:opacity-40"
            )}
          >
            {Icon && <Icon className="w-2.5 h-2.5" />}
            {label}
          </button>
        ))}
      </div>
    );
  };

  const UserCard = ({ u }: { u: UserRecord }) => {
    const isSelf = u.email === user?.email;
    const isMasterAdminUser = u.role === "master_admin";
    const isAdminUser = u.role === "admin" || isMasterAdminUser;
    const isModerator = u.role === "moderator";
    const showUserActionStrip =
      !isAdminUser ||
      (currentUserIsMasterAdmin && !isSelf) ||
      (currentUserIsMasterAdmin && isSelf && isMasterAdminUser && masterAdminCount > 1);

    return (
      <div className={cn(
        "border rounded-md bg-card/50 overflow-hidden",
        isSelf ? "border-amber-400/40" : "border-border"
      )}>
        {/* ── Top row: name / email / date on left, badges + delete on right ── */}
        <div className="flex items-start justify-between px-4 pt-4 pb-3 gap-4">
          <div className="space-y-1 min-w-0 flex-1">
            {/* Name + inline icon buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setStatsUser(u)}
                className="font-medium text-foreground hover:text-primary transition-colors text-left group flex items-center gap-1.5"
                title="View user statistics"
              >
                {u.fullName}
                <BarChart3 className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity text-primary" />
              </button>
              {!isAdminUser && (
                <button
                  onClick={() => setMessageUser(u)}
                  title="Send message to user"
                  className="text-muted-foreground/40 hover:text-primary transition-colors p-0.5 rounded"
                >
                  <Mail className="w-3.5 h-3.5" />
                </button>
              )}
              {isSelf && (
                <span className="text-[10px] font-mono bg-amber-400/20 text-amber-300 border border-amber-400/30 rounded px-1.5 py-0.5">YOU</span>
              )}
              {isMasterAdminUser && (
                <span className="text-[10px] font-mono bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                  <Crown className="w-2.5 h-2.5" />MASTER ADMIN
                </span>
              )}
              {!isMasterAdminUser && isAdminUser && (
                <span className="text-[10px] font-mono bg-primary/20 text-primary border border-primary/30 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                  <ShieldCheck className="w-2.5 h-2.5" />ADMIN
                </span>
              )}
              {isModerator && (
                <span className="text-[10px] font-mono bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded px-1.5 py-0.5 flex items-center gap-0.5">
                  <ShieldCheck className="w-2.5 h-2.5" />MODERATOR
                </span>
              )}
            </div>
            <div className="text-sm text-muted-foreground font-mono">{u.email}</div>
            {(u.phone || u.country) && (
              <div className="flex items-center gap-3 flex-wrap">
                {u.phone && (
                  <span className="text-xs text-muted-foreground/60 font-mono flex items-center gap-1">
                    <Phone className="w-3 h-3" />{u.phone}
                  </span>
                )}
                {u.country && (
                  <span className="text-xs text-muted-foreground/60 font-mono flex items-center gap-1">
                    <Globe className="w-3 h-3" />{u.country}
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground/60 font-mono">
              Registered {new Date(u.createdAt).toLocaleDateString()}
            </div>
          </div>

          {/* Right: plan badge + status badge + lock badge + delete — no action buttons here */}
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            {isAdminUser ? (
              <PlanBadge plan={normalizeTierPlan(u.plan)} />
            ) : (
              <>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-muted-foreground/70">CPL</span>
                  <PlanBadge plan={normalizeTierPlan(u.plan)} />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-mono text-muted-foreground/70">PPL</span>
                  <PlanBadge plan={normalizeTierPlan(u.pplPlan)} />
                </div>
              </>
            )}
            {!isAdminUser && statusBadge(u.status)}
            {u.lockedAt && (
              <Badge className="bg-amber-400/20 text-amber-400 border-amber-400/30 font-mono text-xs" title={`Locked: ${u.lockReason ?? "unknown"}`}>
                🔒 LOCKED
              </Badge>
            )}
            {currentUserIsMasterAdmin && !isSelf && (!isMasterAdminUser || masterAdminCount > 1) && (
              <button
                title="Permanently delete this user"
                disabled={updating === u.id}
                onClick={() => setConfirmDelete(u)}
                className="p-1.5 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-40"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Action buttons row ── */}
        {showUserActionStrip && (
          <div className="border-t border-border/40 px-4 py-2.5 bg-secondary/10 flex gap-2 flex-wrap">
            {currentUserIsMasterAdmin && isSelf && isMasterAdminUser && masterAdminCount > 1 && (
              <>
                <span className="text-[10px] font-mono text-muted-foreground self-center mr-1">MASTER:</span>
                <Button size="sm" variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "user" })}
                  title="Step down to regular user (another master admin must exist)"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />STEP DOWN → USER
                </Button>
                <Button size="sm" variant="outline"
                  className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "moderator" })}
                  title="Step down to moderator"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />STEP DOWN → MOD
                </Button>
                <Button size="sm" variant="outline"
                  className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "admin" })}
                  title="Step down to standard admin"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />STEP DOWN → ADMIN
                </Button>
              </>
            )}
            {!isAdminUser && u.status !== "approved" && (
              <Button size="sm" variant="outline"
                className="border-primary/40 text-primary hover:bg-primary/10 font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => updateStatus.mutate({ id: u.id, status: "approved" })}
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />APPROVE
              </Button>
            )}
            {!isAdminUser && u.status !== "rejected" && (
              <Button size="sm" variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => updateStatus.mutate({ id: u.id, status: "rejected" })}
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />REJECT
              </Button>
            )}
            {!isAdminUser && u.status !== "pending" && (
              <Button size="sm" variant="outline"
                className="border-border text-muted-foreground hover:bg-secondary font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => updateStatus.mutate({ id: u.id, status: "pending" })}
              >
                <Clock className="w-3.5 h-3.5 mr-1" />RESET
              </Button>
            )}
            {!isAdminUser && (
              <Button size="sm" variant="outline"
                className={cn(
                  "font-mono text-xs h-8 px-3",
                  isModerator
                    ? "border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                    : "border-border text-muted-foreground hover:bg-secondary"
                )}
                disabled={updating === u.id}
                onClick={() => updateRole.mutate({ id: u.id, role: isModerator ? "user" : "moderator" })}
                title={isModerator ? "Remove moderator role" : "Promote to moderator (support tickets access)"}
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                {isModerator ? "REMOVE MOD" : "MAKE MOD"}
              </Button>
            )}
            {/* Lock / Unlock */}
            {!isAdminUser && (u.lockedAt ? (
              <Button size="sm" variant="outline"
                className="border-red-500/40 text-red-400 hover:bg-red-500/10 font-mono text-xs h-8 px-2.5"
                disabled={updating === u.id}
                onClick={() => lockUser.mutate({ id: u.id, action: "unlock" })}
                title={`Account locked (${u.lockReason ?? "unknown"}) — click to unlock`}
              >
                <Lock className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button size="sm" variant="outline"
                className="border-border text-muted-foreground hover:border-red-500/40 hover:text-red-400 hover:bg-red-500/10 font-mono text-xs h-8 px-2.5"
                disabled={updating === u.id}
                onClick={() => lockUser.mutate({ id: u.id, action: "lock" })}
                title="Account is open — click to lock"
              >
                <LockOpen className="w-3.5 h-3.5" />
              </Button>
            ))}
            {/* MAKE ADMIN — master admin only */}
            {currentUserIsMasterAdmin && !isSelf && !isAdminUser && (
              <Button size="sm" variant="outline"
                className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => setConfirmMakeAdmin(u)}
                title="Promote this user to admin"
              >
                <Crown className="w-3.5 h-3.5 mr-1" />MAKE ADMIN
              </Button>
            )}
            {/* REVOKE ADMIN — master admin only, for admin users */}
            {currentUserIsMasterAdmin && !isSelf && isAdminUser && !isMasterAdminUser && (
              <Button size="sm" variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => updateRole.mutate({ id: u.id, role: "user" })}
                title="Revoke admin access — demote back to regular user"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />REVOKE ADMIN
              </Button>
            )}
            {currentUserIsMasterAdmin && !isSelf && isAdminUser && !isMasterAdminUser && (
              <Button size="sm" variant="outline"
                className="border-amber-500/50 text-amber-300 hover:bg-amber-500/15 font-mono text-xs h-8 px-3"
                disabled={updating === u.id}
                onClick={() => updateRole.mutate({ id: u.id, role: "master_admin" })}
                title="Promote this standard admin to co–master admin (full master powers)"
              >
                <Crown className="w-3.5 h-3.5 mr-1" />PROMOTE TO CO-MASTER
              </Button>
            )}
            {/* Demote another master admin (requires at least two masters in the roster) */}
            {currentUserIsMasterAdmin && !isSelf && isMasterAdminUser && masterAdminCount > 1 && (
              <>
                <Button size="sm" variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "user" })}
                  title="Remove master admin — demote to regular user"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />DEMOTE MASTER → USER
                </Button>
                <Button size="sm" variant="outline"
                  className="border-blue-500/40 text-blue-400 hover:bg-blue-500/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "moderator" })}
                  title="Demote to moderator"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />DEMOTE MASTER → MOD
                </Button>
                <Button size="sm" variant="outline"
                  className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 font-mono text-xs h-8 px-3"
                  disabled={updating === u.id}
                  onClick={() => updateRole.mutate({ id: u.id, role: "admin" })}
                  title="Demote to standard admin (not master)"
                >
                  <ShieldCheck className="w-3.5 h-3.5 mr-1" />DEMOTE MASTER → ADMIN
                </Button>
              </>
            )}
          </div>
        )}

        {/* Plan controls row — CPL written prep + PPL bank (foundation for PPL Pro / Pro+) */}
        {!isAdminUser && (
          <div className="border-t border-border/50 px-4 py-2.5 bg-secondary/20 space-y-2">
            <PlanControls u={u} billingTrack="cpl" />
            <PlanControls u={u} billingTrack="ppl" />
          </div>
        )}

        {(u.gender || u.age || u.address || u.pilotType || u.ratings?.length) && (
          <div className="border-t border-border/30 px-4 py-2.5 bg-secondary/10 flex flex-wrap gap-x-6 gap-y-1">
            {u.gender && <span className="text-xs font-mono text-muted-foreground"><span className="text-muted-foreground/50">Gender:</span> {u.gender}</span>}
            {u.age && <span className="text-xs font-mono text-muted-foreground"><span className="text-muted-foreground/50">Age:</span> {u.age}</span>}
            {u.pilotType && <span className="text-xs font-mono text-muted-foreground"><span className="text-muted-foreground/50">Licence:</span> {u.pilotType}</span>}
            {u.address && <span className="text-xs font-mono text-muted-foreground"><span className="text-muted-foreground/50">Address:</span> {u.address}</span>}
            {u.ratings?.length ? (
              <span className="text-xs font-mono text-muted-foreground w-full">
                <span className="text-muted-foreground/50">Ratings:</span> {u.ratings.join(", ")}
              </span>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  const mainTabs: { key: AdminMainTab; label: string; icon: typeof Users | typeof CreditCard | typeof Activity; desc: string }[] = [
    { key: "users", label: "Users", icon: Users, desc: "Roster, plans, new-user approval" },
    { key: "subscriptions", label: "Subscriptions & marketing", icon: CreditCard, desc: "Pricing page style, outreach" },
    { key: "programStats", label: "Program statistics", icon: Activity, desc: "Per-track usage, plans, toggles" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">Admin Panel</h1>
          <p className="text-muted-foreground font-mono text-sm mt-1">USERS · BILLING & MARKETING · PROGRAM ANALYTICS</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 border-b border-border pb-0">
          {mainTabs.map(({ key, label, icon: Icon, desc }) => (
            <button
              key={key}
              type="button"
              onClick={() => setMainTab(key)}
              className={cn(
                "flex items-start gap-3 rounded-t-lg border border-b-0 px-4 py-3 text-left transition-colors sm:flex-1",
                mainTab === key
                  ? "border-border bg-card border-b-card -mb-px z-[1] shadow-sm"
                  : "border-transparent bg-secondary/20 text-muted-foreground hover:text-foreground hover:bg-secondary/40",
              )}
            >
              <Icon className={cn("w-5 h-5 shrink-0 mt-0.5", mainTab === key ? "text-primary" : "")} />
              <span>
                <span className="block text-sm font-bold font-mono tracking-wide">{label}</span>
                <span className="block text-[11px] text-muted-foreground font-mono mt-0.5 leading-snug">{desc}</span>
              </span>
            </button>
          ))}
        </div>

        {mainTab === "subscriptions" && (
          <div className="space-y-4">
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-bold font-mono text-foreground tracking-wide">SUBSCRIPTION PAGE (CPL CHECKOUT)</span>
                </div>
                <div className="text-sm font-medium text-foreground">Subscription page style</div>
                <div className="text-xs text-muted-foreground mt-0.5 mb-2">
                  Choose which pricing page design users see (Classic / Premium / Platinum).
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => updateSetting.mutate({ subscription_style: "classic" })}
                      disabled={updateSetting.isPending || !settings || subscriptionStyle === "classic"}
                      className={cn(
                        "px-3 py-1.5 rounded border text-xs font-mono",
                        subscriptionStyle === "classic"
                          ? "border-primary/50 bg-primary/15 text-primary"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                      )}
                    >
                      CLASSIC
                    </button>
                    <button
                      onClick={() => updateSetting.mutate({ subscription_style: "premium" })}
                      disabled={updateSetting.isPending || !settings || subscriptionStyle === "premium"}
                      className={cn(
                        "px-3 py-1.5 rounded border text-xs font-mono",
                        subscriptionStyle === "premium"
                          ? "border-amber-400/50 bg-amber-400/15 text-amber-300"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-amber-400/40"
                      )}
                    >
                      PREMIUM
                    </button>
                    <button
                      onClick={() => updateSetting.mutate({ subscription_style: "platinum" })}
                      disabled={updateSetting.isPending || !settings || subscriptionStyle === "platinum"}
                      className={cn(
                        "px-3 py-1.5 rounded border text-xs font-mono",
                        subscriptionStyle === "platinum"
                          ? "border-fuchsia-400/50 bg-fuchsia-500/15 text-fuchsia-300"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-fuchsia-400/40"
                      )}
                    >
                      PLATINUM
                    </button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-bold font-mono text-foreground tracking-wide">MARKETING & OUTREACH</span>
                </div>
                <p className="text-xs text-muted-foreground font-mono leading-relaxed">
                  Ad campaigns and copy changes still ship through your usual channels; use these tools for inbound leads and public messaging.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link href="/admin/public-contacts">
                    <Button variant="outline" size="sm" className="font-mono text-xs gap-2">
                      <Mail className="w-3.5 h-3.5" />
                      Public contact inbox
                    </Button>
                  </Link>
                  <Link href="/admin/tickets">
                    <Button variant="outline" size="sm" className="font-mono text-xs gap-2">
                      <Shield className="w-3.5 h-3.5" />
                      Support tickets
                    </Button>
                  </Link>
                  <Link href="/contact">
                    <Button variant="outline" size="sm" className="font-mono text-xs gap-2">
                      <Globe className="w-3.5 h-3.5" />
                      Public contact page
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {mainTab === "programStats" && (
          <div className="space-y-4">
            <Card className="border-border bg-card">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />
                  <span className="text-sm font-bold font-mono text-foreground tracking-wide">ACCOUNTS (APPROVED / PENDING)</span>
                </div>
                {programStatsLoading ? (
                  <p className="text-xs font-mono text-muted-foreground animate-pulse">Loading aggregate stats…</p>
                ) : programStats ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-md border border-border bg-secondary/20 px-3 py-2">
                      <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Approved</div>
                      <div className="text-2xl font-bold font-mono text-foreground">{programStats.totals.approvedUsers}</div>
                    </div>
                    <div className="rounded-md border border-border bg-secondary/20 px-3 py-2">
                      <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Pending</div>
                      <div className="text-2xl font-bold font-mono text-yellow-400">{programStats.totals.pendingUsers}</div>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-2">
              {(programStats?.tracks ?? []).map((row) => {
                const meta = TRACK_INFO[row.track as LicenseTrack];
                const settingKey =
                  row.track === "cpl" ? "track_cpl_enabled"
                    : row.track === "ppl" ? "track_ppl_enabled"
                      : row.track === "pstar" ? "track_pstar_enabled"
                        : "track_roca_enabled";
                return (
                  <Card key={row.track} className="border-border bg-card overflow-hidden">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className={cn("text-lg font-bold font-mono", meta.color)}>{meta.shortLabel}</div>
                          <div className="text-[11px] text-muted-foreground leading-snug">{meta.fullName}</div>
                        </div>
                        <label className="flex flex-col items-end gap-1 cursor-pointer select-none shrink-0">
                          <span className="text-[9px] font-mono text-muted-foreground uppercase tracking-wider">Visible</span>
                          <button
                            type="button"
                            disabled={updateSetting.isPending || !settings}
                            onClick={() => updateSetting.mutate({ [settingKey]: !row.enabled } as Record<string, unknown>)}
                            className={cn(
                              "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                              "focus:outline-none disabled:opacity-50",
                              row.enabled ? "bg-primary" : "bg-secondary"
                            )}
                            role="switch"
                            aria-checked={row.enabled}
                          >
                            <span
                              className={cn(
                                "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                                row.enabled ? "translate-x-5" : "translate-x-0"
                              )}
                            />
                          </button>
                        </label>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded border border-border/60 bg-secondary/10 px-2 py-2">
                          <div className="text-[9px] font-mono text-muted-foreground uppercase">Questions</div>
                          <div className="text-lg font-bold font-mono">{row.questionsInBank}</div>
                        </div>
                        <div className="rounded border border-border/60 bg-secondary/10 px-2 py-2">
                          <div className="text-[9px] font-mono text-muted-foreground uppercase">Learners</div>
                          <div className="text-lg font-bold font-mono">{row.distinctLearners}</div>
                        </div>
                        <div className="rounded border border-border/60 bg-secondary/10 px-2 py-2">
                          <div className="text-[9px] font-mono text-muted-foreground uppercase">Done</div>
                          <div className="text-lg font-bold font-mono">{row.completedSessions}</div>
                        </div>
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono leading-relaxed">
                        Learners = distinct non-staff accounts with ≥1 session on this track. “Done” = completed sessions (same scope).
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {programStats && (
              <div className="grid gap-4 md:grid-cols-2">
                <Card className="border-border bg-card">
                  <CardContent className="p-4 space-y-2">
                    <div className="text-sm font-bold font-mono text-foreground">CPL plan (approved non-staff)</div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs font-mono">
                      <div><span className="text-muted-foreground">Free</span><div className="text-lg font-bold">{programStats.cplPlanAmongApprovedLearners.free}</div></div>
                      <div><span className="text-muted-foreground">Pro</span><div className="text-lg font-bold text-primary">{programStats.cplPlanAmongApprovedLearners.pro}</div></div>
                      <div><span className="text-muted-foreground">Pro+</span><div className="text-lg font-bold text-amber-300">{programStats.cplPlanAmongApprovedLearners.pro_plus}</div></div>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono">Written prep billing (`plan` column). Stripe checkout hooks up here.</p>
                  </CardContent>
                </Card>
                <Card className="border-border bg-card">
                  <CardContent className="p-4 space-y-2">
                    <div className="text-sm font-bold font-mono text-foreground">PPL plan (approved non-staff)</div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs font-mono">
                      <div><span className="text-muted-foreground">Free</span><div className="text-lg font-bold">{programStats.pplPlanAmongApprovedLearners.free}</div></div>
                      <div><span className="text-muted-foreground">Pro</span><div className="text-lg font-bold text-primary">{programStats.pplPlanAmongApprovedLearners.pro}</div></div>
                      <div><span className="text-muted-foreground">Pro+</span><div className="text-lg font-bold text-amber-300">{programStats.pplPlanAmongApprovedLearners.pro_plus}</div></div>
                    </div>
                    <p className="text-[10px] text-muted-foreground font-mono">Foundation for PPL Pro / PPL Pro+ (`ppl_plan`). Admin can set per user today; payments later.</p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card className="border-border bg-card">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-bold font-mono text-foreground">Pricing page preview</div>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">Same live page users see — admin preview mode, no charge.</p>
                </div>
                <Link href="/subscription">
                  <Button variant="outline" className="gap-2 border-violet-500/40 text-violet-400 hover:bg-violet-500/10 font-mono text-xs">
                    <Eye className="w-3.5 h-3.5" />
                    Open /subscription
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        )}

        {mainTab === "users" && (
        <>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-bold font-mono text-foreground tracking-wide">NEW USER APPROVAL</span>
            </div>
            <label className="flex items-center justify-between gap-4 cursor-pointer select-none">
              <div>
                <div className="text-sm font-medium text-foreground">Require admin approval for new users</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {requireApproval
                    ? "New sign-ups go into Pending until you approve them on this tab."
                    : "New sign-ups are automatically approved and can log in immediately."}
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateSetting.mutate({ require_approval: !requireApproval })}
                disabled={updateSetting.isPending || !settings}
                className={cn(
                  "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200",
                  "focus:outline-none disabled:opacity-50",
                  requireApproval ? "bg-primary" : "bg-secondary"
                )}
                role="switch"
                aria-checked={requireApproval}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200",
                    requireApproval ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </label>
          </CardContent>
        </Card>

        {/* User groups — one selector row (count + filter, no duplicate stats bar) */}
        <div>
          <p className="text-xs font-mono text-muted-foreground mb-2 uppercase tracking-wider">
            Select a group to view
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2">
            {userFilterTabs.map(tab => {
              const Icon = tab.icon;
              const selected = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    resetFilters();
                  }}
                  aria-pressed={selected}
                  className={cn(
                    "flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-all min-h-[4.5rem]",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                    selected
                      ? tab.selectedClass
                      : "border-border bg-card hover:bg-secondary/60 hover:border-primary/25",
                    tab.key === "pending" && tab.count > 0 && !selected && "border-yellow-500/35",
                  )}
                >
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <Icon className={cn("w-4 h-4 shrink-0", selected ? "opacity-100" : tab.accent)} />
                    <span
                      className={cn(
                        "text-[11px] font-mono font-semibold uppercase tracking-wide truncate",
                        selected ? "" : "text-muted-foreground",
                      )}
                    >
                      {tab.label}
                    </span>
                  </div>
                  <span
                    className={cn(
                      "text-2xl font-bold font-mono tabular-nums leading-none",
                      selected ? "" : tab.accent,
                    )}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Search + Filter bar ── */}
        <div className="space-y-2">
          {/* Row 1: Search input + action buttons */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search name, email, country, pilot type…"
                className={cn(
                  "w-full pl-9 pr-9 py-2.5 text-sm font-mono rounded-md border bg-secondary/30 text-foreground placeholder:text-muted-foreground/40",
                  "border-border focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-colors"
                )}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Toggle filters */}
            <button
              onClick={() => setShowFilters(v => !v)}
              title="Toggle filters"
              className={cn(
                "flex items-center gap-1.5 px-3 py-2.5 rounded-md border text-sm font-mono whitespace-nowrap transition-colors",
                showFilters || activeFilterCount > 0
                  ? "border-primary/60 bg-primary/10 text-primary"
                  : "border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/40"
              )}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="bg-primary text-background text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Sort */}
            <div className="relative">
              <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50 pointer-events-none" />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value as typeof sortBy)}
                className="pl-8 pr-3 py-2.5 text-xs font-mono rounded-md border border-border bg-secondary/30 text-foreground focus:border-primary/50 focus:outline-none appearance-none cursor-pointer"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="name_asc">Name A → Z</option>
                <option value="name_desc">Name Z → A</option>
                <option value="attempts">Failed attempts ↓</option>
              </select>
            </div>

            {/* Export */}
            <button
              title="Export filtered users as CSV"
              onClick={async () => {
                const baseUrl = (import.meta.env.BASE_URL ?? "").replace(/\/$/, "");
                const token = localStorage.getItem("token") ?? "";
                const res = await fetch(`${baseUrl}/api/admin/users/export`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `flightdeck-users-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-md border border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-primary/50 hover:bg-secondary/60 transition-colors text-sm font-mono whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>

          {/* Row 2: Filter panel (collapsible) */}
          {showFilters && (
            <div className="border border-border/60 rounded-md bg-secondary/10 p-3 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">

                {/* Plan filter */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-muted-foreground/60 tracking-widest uppercase">CPL Plan</label>
                  <select
                    value={filterPlan}
                    onChange={e => setFilterPlan(e.target.value as typeof filterPlan)}
                    className="w-full px-2 py-1.5 text-xs font-mono rounded border border-border bg-secondary/40 text-foreground focus:border-primary/50 focus:outline-none"
                  >
                    <option value="all">All plans</option>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="pro_plus">Pro Plus</option>
                  </select>
                </div>

                {/* Lock state */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-muted-foreground/60 tracking-widest uppercase">Account Status</label>
                  <select
                    value={filterLock}
                    onChange={e => setFilterLock(e.target.value as typeof filterLock)}
                    className="w-full px-2 py-1.5 text-xs font-mono rounded border border-border bg-secondary/40 text-foreground focus:border-primary/50 focus:outline-none"
                  >
                    <option value="all">All accounts</option>
                    <option value="locked">🔒 Locked only</option>
                    <option value="unlocked">🔓 Unlocked only</option>
                  </select>
                </div>

                {/* Pilot type */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-muted-foreground/60 tracking-widest uppercase">Pilot Type</label>
                  <select
                    value={filterPilotType}
                    onChange={e => setFilterPilotType(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs font-mono rounded border border-border bg-secondary/40 text-foreground focus:border-primary/50 focus:outline-none"
                  >
                    <option value="all">All types</option>
                    {uniquePilotTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Country */}
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-muted-foreground/60 tracking-widest uppercase">Country</label>
                  <select
                    value={filterCountry}
                    onChange={e => setFilterCountry(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs font-mono rounded border border-border bg-secondary/40 text-foreground focus:border-primary/50 focus:outline-none"
                  >
                    <option value="all">All countries</option>
                    {uniqueCountries.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Reset + result summary */}
              <div className="flex items-center justify-between pt-1 border-t border-border/40">
                <p className="text-xs text-muted-foreground font-mono">
                  <span className="text-foreground font-semibold">{filteredUsers.length}</span>
                  {" "}of{" "}
                  <span>{baseList.length}</span>
                  {" "}user{baseList.length !== 1 ? "s" : ""} shown
                </p>
                {(activeFilterCount > 0 || searchQuery) && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-xs font-mono text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" /> Reset all filters
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Compact result count when filters are hidden */}
          {!showFilters && (searchQuery || activeFilterCount > 0) && (
            <p className="text-xs text-muted-foreground font-mono flex items-center gap-3">
              <span>
                <span className="text-foreground font-semibold">{filteredUsers.length}</span>
                {" "}of {baseList.length} users
              </span>
              <button onClick={resetFilters} className="text-muted-foreground/60 hover:text-destructive transition-colors flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> clear
              </button>
            </p>
          )}
        </div>

        {/* User List */}
        {isLoading ? (
          <div className="text-primary font-mono text-sm animate-pulse tracking-widest">LOADING ROSTER...</div>
        ) : filteredUsers.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-mono text-sm">
                {searchQuery
                  ? `No users match "${searchQuery}" in this tab.`
                  : `No ${activeTab === "all" ? "" : activeTab} users found.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map(u => <UserCard key={u.id} u={u} />)}
          </div>
        )}
        </>
        )}

      {/* Delete Confirmation Dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDelete(null)} />
          <div className="relative z-10 bg-card border border-destructive/40 rounded-lg p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-destructive/20 p-2 rounded-md">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <h2 className="font-bold text-foreground font-mono tracking-wide">CONFIRM DELETE</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              This will <span className="text-destructive font-semibold">permanently delete</span> this user. This action cannot be undone.
            </p>
            <div className="bg-secondary/50 rounded-md p-3 mb-5 font-mono text-sm">
              <div className="text-foreground">{confirmDelete.fullName}</div>
              <div className="text-muted-foreground text-xs">{confirmDelete.email}</div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 font-mono"
                onClick={() => setConfirmDelete(null)}
                disabled={updating === confirmDelete.id}
              >
                CANCEL
              </Button>
              <Button
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-mono"
                disabled={updating === confirmDelete.id}
                onClick={() => deleteUser.mutate(confirmDelete.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                DELETE
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Make Admin Confirmation Dialog */}
      {confirmMakeAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmMakeAdmin(null)} />
          <div className="relative z-10 bg-card border border-amber-500/40 rounded-lg p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-500/20 p-2 rounded-md">
                <Crown className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="font-bold text-foreground font-mono tracking-wide">CONFIRM ADMIN PROMOTION</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              This will grant <span className="text-amber-400 font-semibold">full admin access</span> to this user. They will be able to manage all users, settings, and content.
            </p>
            <div className="bg-secondary/50 rounded-md p-3 mb-5 font-mono text-sm">
              <div className="text-foreground">{confirmMakeAdmin.fullName}</div>
              <div className="text-muted-foreground text-xs">{confirmMakeAdmin.email}</div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 font-mono"
                onClick={() => setConfirmMakeAdmin(null)}
                disabled={updating === confirmMakeAdmin.id}
              >
                CANCEL
              </Button>
              <Button
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-mono font-bold"
                disabled={updating === confirmMakeAdmin.id}
                onClick={() => {
                  updateRole.mutate({ id: confirmMakeAdmin.id, role: "admin" });
                  setConfirmMakeAdmin(null);
                }}
              >
                <Crown className="w-4 h-4 mr-2" />MAKE ADMIN
              </Button>
            </div>
          </div>
        </div>
      )}

      {statsUser && (
        <UserStatsModal
          userId={statsUser.id}
          userName={statsUser.fullName}
          userEmail={statsUser.email}
          onClose={() => setStatsUser(null)}
        />
      )}

      {messageUser && (
        <UserMessageModal
          userId={messageUser.id}
          userName={messageUser.fullName}
          userEmail={messageUser.email}
          onClose={() => setMessageUser(null)}
        />
      )}
      </div>
    </Layout>
  );
}
